import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/database';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { FlashMessagesService } from '@mobilestar/ngx-flash-messages';
import { Account } from '@models/Account';
import { AccountService } from '@services/account.service';
import { LoaderService } from '@services/support/loader.service';
import { ModalService } from '@services/support/modal.service';
import { Subscription } from 'rxjs';
import { first } from 'rxjs/operators';

@Component({
  selector: 'app-usage-reports',
  templateUrl: './usage-reports.component.html',
  styleUrls: ['./usage-reports.component.scss']
})
export class UsageReportsComponent implements OnInit {

  @ViewChild("getReportNowTemplate", { static: true }) private getReportNowTemplate: TemplateRef<any>;
  @ViewChild("accountFilterTemplate", { static: true }) private accountFilterTemplate: TemplateRef<any>;

  licenseTypes = AccountService.licenseTypes
  licenseModels = AccountService.licenseModels
  allFeatures = AccountService.allFeatures
  allAddOns = AccountService.allAddOns
  timezones = AccountService.timezones

  TIMEZONES = ["UTC-12", "UTC-11", "UTC-10", "UTC-9", "UTC-8", "UTC-7", "UTC-6", "UTC-5", "UTC-4", "UTC-3", "UTC-2", "UTC-1", "UTC",
  "UTC+1", "UTC+2", "UTC+3", "UTC+4", "UTC+5", "UTC+6", "UTC+7", "UTC+8", "UTC+9", "UTC+10", "UTC+11", "UTC+12", "UTC+13", "UTC+14"]

  usageRecipientsForm = null

  validMailValidator = Validators.pattern(/^$|^\w+([.-]?\w+)*@\w+([.-]?\w+)*(.\w{2,})+$/);

  accountList = []

  filterValues = {
    demoOrNot: 'all',
    accountType: 'all',
    filterTimezones: [],
    filterLicenseTypes: [],
    filterLicenseModels: [],
    hideAccountsWithNoLicense: true,
    hideDisabled: true,
    filterManagers: [],
    haveFeature: [],
    noFeature: [],
    haveAddOn: [],
    noAddOn: [],
    minSessionCount: null,
    minSessionDuration: null,
    minUserCount: null,
    minRoomCount: null
  };
  filters: {[key: string]: (account: Account) => boolean} = {
    hideDisabled: a => a.disabled !== true,
    hideAccountsWithNoLicense: a => a.no_license !== true
  };
  unFilteredAccounts: Account[] = null;
  accounts: Account[] = null;
  accountIds: string[] = []

  accountsMetaSub: Subscription = null;

  accountManagers = null;
  accountsManagersSub: Subscription = null;

  constructor(
    private accountService: AccountService,
    private afdb: AngularFireDatabase,
    private modalService: ModalService,
    private flashMessages: FlashMessagesService,
    private loaderService: LoaderService
  ) { }

  ngOnInit() {
    this.accountService.accountsMeta.subscribe(accounts => {
      this.accountList = accounts.map(a => { return { id: a.id, company_name: `${a.company_name} (${a.account_name})` } })
      this.unFilteredAccounts = accounts.sort((a, b) => a.company_name.toLocaleLowerCase().localeCompare(b.company_name.toLocaleLowerCase()));
      this.applyAccountFilters();
    })
    this.accountsManagersSub = this.accountService.accountsManagers.subscribe(managers => {
      this.accountManagers = managers;
    })

    this.loadRecipients()
  }

  loadRecipients() {
    this.afdb.object(`weekly_usage_report_recipients`).valueChanges().pipe(first()).toPromise().then(recipients => {
      if (recipients) {
        const recIds = Object.keys(recipients)
        const rList = []
        for (const rid of recIds) {
          rList.push(new FormGroup({
            id: new FormControl(rid),
            emails: new FormControl(recipients[rid].emails.map(em => { return { display: em, value: em }}), [Validators.required]),
            accounts: new FormControl(recipients[rid].accounts, [Validators.required]),
            timezone: new FormControl(recipients[rid].timezone)
          }))
        }
        this.usageRecipientsForm = new FormGroup({ recipients: new FormArray(rList) })
      } else {
        this.usageRecipientsForm = new FormGroup({ recipients: new FormArray([]) })
      }
    })
  }

  addNewRecipient() {
    const recipients = this.usageRecipientsForm.controls.recipients as FormArray
    const rec = new FormGroup({
      id: new FormControl(this.afdb.createPushId()),
      emails: new FormControl([], [Validators.required]),
      accounts: new FormControl([], [Validators.required]),
      timezone: new FormControl("UTC")
    })
    recipients.push(rec)
  }

  removeRecipient(recIndex: number) {
    const recipients = this.usageRecipientsForm.controls.recipients as FormArray
    recipients.removeAt(recIndex)
  }

  usageRecipientsFormSubmit() {
    const usageRecipients: any = {}
    const recipients = this.usageRecipientsForm.controls.recipients as FormArray
    for (const rec of recipients.controls) {
      const id = (rec as FormGroup).controls.id.value
      usageRecipients[id] = {
        emails: (rec as FormGroup).controls.emails.value.map(item => item.value),
        accounts: (rec as FormGroup).controls.accounts.value,
        timezone: (rec as FormGroup).controls.timezone.value
      }
    }
    this.loaderService.show()
    this.afdb.object(`weekly_usage_report_recipients`).set(usageRecipients)
      .then(() => this.flashMessages.show("Recipients saved", { cssClass: 'alert-success', timeout: 4000, closeOnClick: true }))
      .catch(error => {
        console.log(error)
        this.flashMessages.show("An error occurred", { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
      })
      .finally(() => this.loaderService.hide())
  }

  getReportNow(recipientGroup: FormGroup) {
    const dataModel = {
      reportStartEnd: []
    };
    const modalId = this.modalService.show({
      template: this.getReportNowTemplate,
      context: {
        dataModel: dataModel,
        callbacks: {
          close: () => this.modalService.hide(modalId),
          get: () => {
            const emails = recipientGroup.controls.emails.value.map(item => item.value)
            const accounts = recipientGroup.controls.accounts.value
            const timezone = recipientGroup.controls.timezone.value

            this.loaderService.show()
            this.accountService.getManagerReport(emails, accounts, timezone, dataModel.reportStartEnd)
              .then(() => {
                this.modalService.hide(modalId)
                this.flashMessages.show("Report sent", { cssClass: 'alert-success', timeout: 4000, closeOnClick: true })
              })
              .catch(error => {
                console.log(error)
                this.flashMessages.show("An error occurred", { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
              })
              .finally(() => this.loaderService.hide())
          }
        }
      }
    })
  }

  openFilters(recipientGroup: FormGroup) {
    const modalId = this.modalService.show({
      template: this.accountFilterTemplate,
      context: {
        dataModel: null,
        callbacks: {
          close: () => this.modalService.hide(modalId),
          apply: () => {
            recipientGroup.controls.accounts.patchValue(this.accountIds)
            this.modalService.hide(modalId)
          }
        }
      }
    })
  }

  exportFilterResults() {
    const result = [];
    
    for (const account of this.accounts) {
      const a: any = {
        companyName: account.company_name,
        accountManager: this.accountManagers && account.account_manager ? this.accountManagers.find(acm => acm.email === account.account_manager) : "-",
        accountName: account.account_name,
        createDate: new Date(account.create_time).toLocaleDateString("tr-TR", { timeZone: "Europe/Istanbul" }),
        startDate: new Date(account.license.start).toLocaleDateString("tr-TR", { timeZone: "Europe/Istanbul" }),
        endDate: new Date(account.license.end).toLocaleDateString("tr-TR", { timeZone: "Europe/Istanbul" }),
        accountDuration: `${Math.floor((account.license.end-account.license.start) / 86400000)} days`,
        isTrial: account.license.demo,
        isDisabled: account.disabled ? true : false,
        licenseType: account.license.type,
        licenseModel: account.license.model,
        adminEmail: account.admin.email,
        timeZone: account.timezone,
        noOfUser: account.user_count,
        nofActiveUser: account.active_user_count,
        emailRecipientList: account.email_recipients ? account.email_recipients : [],
        durationTotal: `${account.session_duration_hours} hours ${account.session_duration_minutes} minutes`,
        nofSessionsTotal: account.total_session_count/*,
        nofActiveP2PRooms: "NODATA",
        nofActiveNormalRooms: "NODATA",
        lastAppUsageDate: "NODATA",
        lastAdminUsageDate: "NODATA",
        averageDurationTotal: "NODATA",
        durationLastMonth: "NODATA",
        nofSessionsLastMonth: "NODATA",
        averageDurationLastMonth: "NODATA",
        durationLastWeek: "NODATA",
        nofSessionsLastWeek: "NODATA",
        averageDurationLastWeek: "NODATA",
        maxSessionTime: "NODATA",
        minSessionTime: "NODATA"*/
      };
      if (account.disabled) {
        a.disableTime = account.disable_time;
        /*
        a.nofFailedLoginAttemptsTotal = "NODATA";
        a.nofFailedLoginAttemptsLastMonth = "NODATA";
        a.nofFailedLoginAttemptsLastWeek = "NODATA";
        */
      } else {
        a.remainingAccountDuration = `${Math.floor((account.license.end-Date.now()) / 86400000)} days`;
        if (account.license.demo) {
          a.trialLeftDays = (account.license.end-Date.now()) / 1000 / 60 / 60 / 24;
        }
      }
      if (account.license.type === "expert_user_based") {
        a.nofSubscriber = account.user_count - account.expert_users.count;
        a.nofExpert = account.expert_users.count;
        a.nofExpertLimit = account.license.limit2;
        a.expertUtilization = account.license.limit2 === 0 ? "0%" : Math.floor((account.expert_users.count / account.license.limit2)*100)+"%";
      }
      result.push(a);
    }
    const jsonStr = JSON.stringify(result, null, 2);
    const blob = new Blob([jsonStr], {type: 'text/plain'});
    saveAs(blob, "export.json");
  }

  demoOrNotFilterChanged() {
    if (this.filterValues.demoOrNot !== 'all') {
      this.filters.demoOrNot = a => this.filterValues.demoOrNot === 'demo' ? a.license.demo : !a.license.demo
    } else if (this.filters.demoOrNot) {
      delete this.filters.demoOrNot;
    }
    this.applyAccountFilters();
  }

  accountTypeFilterChanged() {
    if (this.filterValues.accountType !== 'all') {
      this.filters.accountType = a => this.filterValues.accountType === 'remote' ? a.license.remote_account : a.license.workflow_account
    } else if (this.filters.accountType) {
      delete this.filters.accountType;
    }
    this.applyAccountFilters();
  }

  managerFilterChanged() {
    if (this.filterValues.filterManagers.length > 0) {
      this.filters.managerFilter = a => this.filterValues.filterManagers.includes(a.account_manager)
    } else if (this.filters.managerFilter) {
      delete this.filters.managerFilter;
    }
    this.applyAccountFilters();
  }

  filterTimezonesChanged() {
    if (this.filterValues.filterTimezones.length > 0) {
      this.filters.filterTimezones = a => this.filterValues.filterTimezones.includes(a.timezone)
    } else if (this.filters.filterTimezones) {
      delete this.filters.filterTimezones;
    }
    this.applyAccountFilters();
  }

  filterLicenseTypesChanged() {
    if (this.filterValues.filterLicenseTypes.length > 0) {
      this.filters.filterLicenseTypes = a => this.filterValues.filterLicenseTypes.includes(a.license.type)
    } else if (this.filters.filterLicenseTypes) {
      delete this.filters.filterLicenseTypes;
    }
    this.applyAccountFilters();
  }

  filterLicenseModelsChanged() {
    if (this.filterValues.filterLicenseModels.length > 0) {
      this.filters.filterLicenseModels = a => this.filterValues.filterLicenseModels.includes(a.license.model)
    } else if (this.filters.filterLicenseModels) {
      delete this.filters.filterLicenseModels;
    }
    this.applyAccountFilters();
  }

  haveFeatureFilterChanged() {
    if (this.filterValues.haveFeature.length > 0) {
      this.filters.haveFeature = a => this.filterValues.haveFeature.every(f => a.features[f])
    } else if (this.filters.haveFeature) {
      delete this.filters.haveFeature;
    }
    this.applyAccountFilters();
  }

  noFeatureFilterChanged() {
    if (this.filterValues.noFeature.length > 0) {
      this.filters.noFeature = a => this.filterValues.noFeature.every(f => !a.features[f])
    } else if (this.filters.noFeature) {
      delete this.filters.noFeature;
    }
    this.applyAccountFilters();
  }

  haveAddOnFilterChanged() {
    if (this.filterValues.haveAddOn.length > 0) {
      this.filters.haveAddOn = a => this.filterValues.haveAddOn.every(ao => a.add_ons[ao])
    } else if (this.filters.haveAddOn) {
      delete this.filters.haveAddOn;
    }
    this.applyAccountFilters();
  }

  noAddOnFilterChanged() {
    if (this.filterValues.noAddOn.length > 0) {
      this.filters.noAddOn = a => this.filterValues.noAddOn.every(ao => !a.add_ons[ao])
    } else if (this.filters.noAddOn) {
      delete this.filters.noAddOn;
    }
    this.applyAccountFilters();
  }

  accountPropertyEquals(name: string, propPath: string, value: any) {
    const deepPath = propPath.split('/');
    if (this.filterValues[name]) {
      this.filters[name] = a => deepPath.reduce((val, p) => val ? val[p] : undefined, a) === value;
    } else if (this.filters[name]) {
      delete this.filters[name];
    }
    this.applyAccountFilters();
  }

  accountPropertyNotEquals(name: string, propPath: string, value: any) {
    const deepPath = propPath.split('/');
    if (this.filterValues[name]) {
      this.filters[name] = a => deepPath.reduce((val, p) => val ? val[p] : undefined, a) !== value;
    } else if (this.filters[name]) {
      delete this.filters[name];
    }
    this.applyAccountFilters();
  }

  accountPropertyGreaterThan(name: string, propPath: string, value: any) {
    const deepPath = propPath.split('/');
    if (this.filterValues[name]) {
      this.filters[name] = a => deepPath.reduce((val, p) => val ? val[p] : undefined, a) > value;
    } else if (this.filters[name]) {
      delete this.filters[name];
    }
    this.applyAccountFilters();
  }

  accountPropertyGreaterEqualThan(name: string, propPath: string, value: any) {
    const deepPath = propPath.split('/');
    if (this.filterValues[name]) {
      this.filters[name] = a => deepPath.reduce((val, p) => val ? val[p] : undefined, a) >= value;
    } else if (this.filters[name]) {
      delete this.filters[name];
    }
    this.applyAccountFilters();
  }

  accountPropertyLessThan(name: string, propPath: string, value: any) {
    const deepPath = propPath.split('/');
    if (this.filterValues[name]) {
      this.filters[name] = a => deepPath.reduce((val, p) => val ? val[p] : undefined, a) < value;
    } else if (this.filters[name]) {
      delete this.filters[name];
    }
    this.applyAccountFilters();
  }

  accountPropertyLessEqualThan(name: string, propPath: string, value: any) {
    const deepPath = propPath.split('/');
    if (this.filterValues[name]) {
      this.filters[name] = a => deepPath.reduce((val, p) => val ? val[p] : undefined, a) <= value;
    } else if (this.filters[name]) {
      delete this.filters[name];
    }
    this.applyAccountFilters();
  }

  applyAccountFilters() {
    let filterIndex = 0;
    let result = true;
    let aid = null;
    const keys = Object.keys(this.filters);
    let filterFn: (a: Account) => boolean = (a) => {
      if (aid !== a.id) {                               // reset temp data if account changed
        aid = a.id;
        filterIndex = 0;
        result = true;
      }
      if (result) {                                     // evaluate previous result
        if (filterIndex < keys.length) {                // if result is true and there is a filter exists to apply
          result = this.filters[keys[filterIndex]](a);  // apply filter
          filterIndex = filterIndex + 1;                // increment filter counter
          return filterFn(a);                           // call self recursively for apply next filter
        } else {                                        // previous filter results true and there is no filter remain
          return true;                                  // return true
        }
      } else {                                          // previous filter results false, so there is no need to apply other filters
        return false;                                   // return false
      }
    }
    this.accounts = this.unFilteredAccounts.filter(filterFn);
    this.accountIds = this.accounts.map(acc => acc.id)
  }

}
